import { inject, Injectable } from '@angular/core'
import { Content, PhaseWithContent } from '@monorepo/golab-generated'
import { ContentJson, ContentJsonCreatorService } from './content-json-creator.service'
import { combineLatest, filter, take } from 'rxjs'
import { IlsModelPartsService } from './ils-model-parts-service'
import { AppListService } from './app-list.service'
import { LabListService } from './lab-list.service'
import { AppLabListService, LowercaseDescription } from './app-lab-list.service'
import { AppConfigurationInformationService } from './app-configuration-information.service'
import { AppIdService } from './app-id.service'

interface AppLabDescription {
  html: string
}

interface AppLabDescriptionsById {
  [appId: string]: AppLabDescription
}

export interface IlsContentJson {
  nonReviewedReferenceData: {
    appDescriptionsById: AppLabDescriptionsById
    labDescriptionsById: AppLabDescriptionsById
  }
  alwaysAvailableContents: ContentJson[]
  phases: PhaseJson[]
}

interface PhaseJson {
  id: string
  title: string
  contents: ContentJson[]
}

interface StopCondition {
  lastPhaseId: string
  stopContentId: string
}

interface CreatIlsJsonOptions {
  upToGeneratorId?: string
  addAllApps?: boolean
}

@Injectable({ providedIn: 'root' })
export class IlsJsonCreatorService {
  private readonly ilsModelService = inject(IlsModelPartsService)
  private readonly appListService = inject(AppListService)
  private readonly labListService = inject(LabListService)
  private readonly contentJsonCreatorService = inject(ContentJsonCreatorService)
  private readonly appConfigurationInformationService = inject(AppConfigurationInformationService)
  private readonly appIdService = inject(AppIdService)

  async createIlsJson(options?: CreatIlsJsonOptions): Promise<IlsContentJson> {
    await this.waitForAppLabListsLoaded()
    const stopCondition = this.findStopCondition(options?.upToGeneratorId)
    const appDescriptionsById = options?.addAllApps
      ? this.listAllAppDescriptionsByAppId()
      : this.createAppDescriptionsById(stopCondition)
    const labDescriptionsById = this.createLabDescriptionsById(stopCondition)
    const ilsJson: IlsContentJson = {
      nonReviewedReferenceData: {
        appDescriptionsById,
        labDescriptionsById,
      },
      alwaysAvailableContents: await this.createContentJsons(
        this.ilsModelService.globalContentList$.value,
        stopCondition,
      ),
      phases: [],
    }
    for (const phase of this.ilsModelService.phaseList$.value) {
      if (!phase.notForStudents) {
        ilsJson.phases.push(await this.createPhaseJson(phase, stopCondition))
      }
      if (phase._id === stopCondition.lastPhaseId) {
        break
      }
    }
    return ilsJson
  }

  private findStopCondition(upToGeneratorId?: string): StopCondition {
    if (upToGeneratorId) {
      const phaseWithContent = this.ilsModelService.findPhaseWithContent(upToGeneratorId)
      if (phaseWithContent) {
        return { lastPhaseId: phaseWithContent._id, stopContentId: upToGeneratorId }
      }
    }
    return { lastPhaseId: '', stopContentId: '' }
  }

  private waitForAppLabListsLoaded(): Promise<void> {
    this.appListService.loadList()
    this.labListService.loadList()
    return new Promise((resolve) => {
      combineLatest([this.appListService.listsLoaded, this.labListService.listsLoaded])
        .pipe(
          filter(([appListsLoaded, labListsLoaded]) => appListsLoaded && labListsLoaded),
          take(1),
        )
        .subscribe(() => resolve())
    })
  }

  private createAppDescriptionsById(stopCondition: StopCondition): AppLabDescriptionsById {
    return this.createAppLabDescriptionsById('app', this.appListService, stopCondition)
  }

  private createLabDescriptionsById(stopCondition: StopCondition): AppLabDescriptionsById {
    return this.createAppLabDescriptionsById('lab', this.labListService, stopCondition)
  }

  private listAllAppDescriptionsByAppId(): AppLabDescriptionsById {
    const appDescriptionsById: AppLabDescriptionsById = {}
    for (const description of this.appListService.descriptionList) {
      const appId = this.appIdService.getAppId(description.embedLink)
      if (appId) {
        const html =
          'longDescription' in description ? (description.longDescription as string) : description.shortDescription
        appDescriptionsById[appId] = { html }
      } else {
        console.warn(`Could not determine appId for description with url ${description.embedLink}`)
      }
    }
    return appDescriptionsById
  }

  private createAppLabDescriptionsById(
    contentType: string,
    appLabListService: AppLabListService<LowercaseDescription>,
    stopCondition: StopCondition,
  ): AppLabDescriptionsById {
    const usedAppsLabs = new Set<string>()
    const checkContentList = (contentList: Content[]) => {
      for (const content of contentList) {
        if (content._id === stopCondition.stopContentId) {
          break
        }
        if (content.contentType === contentType && content.url && !content.notForStudents) {
          usedAppsLabs.add(content.url)
        }
      }
    }
    checkContentList(this.ilsModelService.globalContentList$.value)
    for (const phase of this.ilsModelService.phaseList$.value) {
      if (!phase.notForStudents) {
        checkContentList(phase.contentList)
      }
      if (phase._id === stopCondition.lastPhaseId) {
        break
      }
    }
    const appLabDescriptionsById: AppLabDescriptionsById = {}
    for (const usedAppsLab of usedAppsLabs) {
      const description = appLabListService.getDescriptionByUrl(usedAppsLab)
      if (description) {
        const id =
          contentType === 'app'
            ? (this.appIdService.getAppId(description.embedLink) ?? description._id)
            : description._id
        const html =
          'longDescription' in description ? (description.longDescription as string) : description.shortDescription
        appLabDescriptionsById[id] = { html }
      }
    }
    return appLabDescriptionsById
  }

  private async createPhaseJson(phase: PhaseWithContent, stopCondition: StopCondition): Promise<PhaseJson> {
    return {
      id: phase._id,
      title: phase.title,
      contents: await this.createContentJsons(phase.contentList, stopCondition),
    }
  }

  private async createContentJsons(contents: Content[], stopCondition: StopCondition): Promise<ContentJson[]> {
    const contentJsons: ContentJson[] = []
    for (const content of contents) {
      if (content._id === stopCondition.stopContentId) {
        break
      }
      if (!content.notForStudents) {
        contentJsons.push(await this.contentJsonCreatorService.createContentJson(content))
      }
    }
    return contentJsons
  }
}
